// Failure Tracking executive surface.
//
// Filter sidebar + paginated list + detail drawer with resolve flow.
// URL-synced filters; CSV export with client-side rate-limit feedback
// (the backend enforces 1 req / 60s / user via 429 + Retry-After).

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertOctagon, Download } from "lucide-react";
import { Badge, Button, Card } from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { useAuth } from "@/hooks/useAuth";
import { DEAL_FLOW_PERMISSIONS } from "@/lib/dealFlowPermissions";
import {
  useFailures,
  useFailure,
  useResolveFailure,
  useMarkFailureReviewed,
} from "@/hooks/dealFlow/useDealFlow";
import { failuresApi } from "@/api/dealFlow/client";
import type {
  CriteriaLane,
  FailureListQuery,
  FailureStatus,
} from "@/api/dealFlow/types";
import { FailureFilterSidebar } from "./failures/FailureFilterSidebar";
import { FailureList } from "./failures/FailureList";
import { FailureDetailDrawer } from "./failures/FailureDetailDrawer";

const LANES: CriteriaLane[] = ["A1", "A2", "A3", "A4"];
const STATUSES: FailureStatus[] = ["Open", "Reviewed", "Resolved"];

function parseStatuses(v: string | null): FailureStatus[] {
  if (!v) return [];
  return v.split(",").filter((s): s is FailureStatus => STATUSES.includes(s as FailureStatus));
}

function parseLanes(v: string | null): CriteriaLane[] {
  if (!v) return [];
  return v.split(",").filter((s): s is CriteriaLane => LANES.includes(s as CriteriaLane));
}

export function FailureTrackingPage() {
  const { hasPermission } = useAuth();
  const canResolve = hasPermission(DEAL_FLOW_PERMISSIONS.ResolveFailure);

  const [params, setParams] = useSearchParams();
  const statuses = parseStatuses(params.get("status"));
  const lanes = parseLanes(params.get("lane"));
  const from = params.get("from") ?? undefined;
  const to = params.get("to") ?? undefined;
  const page = Math.max(1, Number(params.get("page") ?? "1"));
  const pageSize = Math.min(100, Math.max(1, Number(params.get("pageSize") ?? "25")));
  const sort = params.get("sort") ?? "createdAt:desc";

  const [openId, setOpenId] = useState<string | null>(null);
  const [csvCooldownUntil, setCsvCooldownUntil] = useState<number | null>(null);

  // Multi-select for status / lane is squashed to single-value query for the
  // backend (the controller accepts one). Multi-select stays a v2 enhancement;
  // for now the sidebar checkbox list is preserved as a UI affordance and we
  // pass the first selected value to the server. (Documented in OOS list.)
  const query: FailureListQuery = useMemo(
    () => ({
      status: statuses[0],
      lane: lanes[0],
      from,
      to,
      page,
      pageSize,
      sort,
    }),
    [statuses, lanes, from, to, page, pageSize, sort],
  );

  const failuresQ = useFailures(query);
  const detailQ = useFailure(openId ?? undefined);
  const resolveM = useResolveFailure();
  const reviewM = useMarkFailureReviewed();

  const setFilter = useCallback(
    (next: Partial<Record<string, string | null>>) => {
      const merged = new URLSearchParams(params);
      for (const [k, v] of Object.entries(next)) {
        if (v === null || v === undefined || v === "") merged.delete(k);
        else merged.set(k, v);
      }
      // Reset to page 1 whenever a non-page filter changes.
      if (Object.keys(next).some((k) => k !== "page")) merged.set("page", "1");
      setParams(merged, { replace: false });
    },
    [params, setParams],
  );

  const handleExportCsv = useCallback(() => {
    const now = Date.now();
    if (csvCooldownUntil && now < csvCooldownUntil) return;
    const url = failuresApi.exportCsvUrl(query);
    // Open in a new tab so the auth cookie is sent and the download starts.
    // Set client-side cooldown to mirror the server's 60s rate-limit; the
    // server is still the source of truth via 429 + Retry-After.
    window.open(url, "_blank");
    setCsvCooldownUntil(now + 60_000);
  }, [csvCooldownUntil, query]);

  // Tick to clear the cooldown badge when the window expires.
  useEffect(() => {
    if (!csvCooldownUntil) return;
    const remaining = csvCooldownUntil - Date.now();
    if (remaining <= 0) {
      setCsvCooldownUntil(null);
      return;
    }
    const t = setTimeout(() => setCsvCooldownUntil(null), remaining);
    return () => clearTimeout(t);
  }, [csvCooldownUntil]);

  const csvDisabled = !!csvCooldownUntil && Date.now() < csvCooldownUntil;

  return (
    <div className="flex flex-col gap-space-4">
      <PageHeader
        eyebrow="Deal Flow"
        title="Failure Tracking"
        actions={
          <Button
            variant="secondary"
            size="sm"
            onClick={handleExportCsv}
            disabled={csvDisabled}
            title={csvDisabled ? "You exported in the last 60 seconds." : "Download filtered failures as CSV"}
            aria-label="Export filtered failures as CSV"
          >
            <Download size={14} strokeWidth={1.5} aria-hidden />
            <span className="ml-space-1">
              {csvDisabled ? "Exported recently" : "Export CSV"}
            </span>
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-space-4">
        <aside className="lg:col-span-3">
          <FailureFilterSidebar
            statuses={statuses}
            lanes={lanes}
            from={from ?? ""}
            to={to ?? ""}
            onChange={setFilter}
          />
        </aside>

        <main className="lg:col-span-9">
          <Card className="p-space-4">
            {failuresQ.isLoading ? (
              <div role="status" aria-live="polite" className="text-gray-mid">
                Loading failures…
              </div>
            ) : failuresQ.isError ? (
              <div role="alert" className="flex items-center gap-space-2 text-status-error">
                <AlertOctagon size={18} aria-hidden />
                <span>Failed to load failures.</span>
              </div>
            ) : (
              <FailureList
                page={failuresQ.data ?? { items: [], page: 1, pageSize, totalCount: 0 }}
                sort={sort}
                onSortChange={(s) => setFilter({ sort: s })}
                onPageChange={(p) => setFilter({ page: String(p) })}
                onPageSizeChange={(s) => setFilter({ pageSize: String(s) })}
                onOpen={(id) => setOpenId(id)}
              />
            )}

            {failuresQ.data?.items.length === 0 && !failuresQ.isLoading && (
              <div className="mt-space-3 text-gray-mid">
                <p>No failures match your filters.</p>
                <button
                  type="button"
                  className="text-gold underline mt-space-2"
                  onClick={() =>
                    setFilter({
                      status: null,
                      lane: null,
                      from: null,
                      to: null,
                      page: "1",
                    })
                  }
                >
                  Clear filters
                </button>
              </div>
            )}
          </Card>
        </main>
      </div>

      <FailureDetailDrawer
        open={!!openId}
        failure={detailQ.data ?? null}
        canResolve={canResolve}
        isLoading={detailQ.isLoading}
        onClose={() => setOpenId(null)}
        onMarkReviewed={async (notes) => {
          if (!openId) return;
          await reviewM.mutateAsync({ id: openId, notes });
          setOpenId(null);
        }}
        onResolve={async (resolutionNotes) => {
          if (!openId) return;
          await resolveM.mutateAsync({
            id: openId,
            body: { resolutionNotes, certifiedResolved: true },
          });
          setOpenId(null);
        }}
      />

      <span aria-live="polite" className="sr-only">
        {failuresQ.data
          ? `${failuresQ.data.totalCount} failures match the current filters.`
          : ""}
      </span>

      {/* Status badge alt-text legend — never color-only. */}
      <span className="sr-only">
        <Badge status="error" /> Open · <Badge status="review" /> Reviewed · <Badge status="finalized" /> Resolved
      </span>
    </div>
  );
}
